import { computed, reactive, ref } from "vue";
import {
  highRiskReportsApi,
  type HighRiskReportConsultableType,
  type HighRiskReportResult
} from "@/vue/entities/high-risk-report";
import { ERROR_MESSAGES, extractErrorCode, resolveErrorMessage } from "./errorMessages";

export type UseHighRiskReportParams = {
  consultableType: HighRiskReportConsultableType;
  consultableId: string;
  // 報告の根拠となる完了済みリスクアセスメント。未入力では報告フローを開始しない（FR-002）。
  riskAssessmentId: string;
  supplementNoteMaxLength: number;
};

// ref ではなく reactive で返すため、テンプレートから `form.errorMessage` のように .value なしで参照できる。
export type HighRiskReportForm = {
  reasonKeys: number[];
  supplementNote: string;
  errorMessage: string | null;
  supplementNoteError: string | null;
  isSubmitting: boolean;
  supplementNoteLength: number;
  overSupplementNoteLimit: boolean;
  submittable: boolean;
  isSelected: (key: number) => boolean;
  toggleReason: (key: number, selected: boolean) => void;
  clearError: () => void;
  submit: () => Promise<HighRiskReportResult | null>;
};

export const useHighRiskReport = (params: UseHighRiskReportParams): HighRiskReportForm => {
  const reasonKeys = ref<number[]>([]);
  const supplementNote = ref("");
  const errorMessage = ref<string | null>(null);
  const supplementNoteError = ref<string | null>(null);
  const isSubmitting = ref(false);

  const supplementNoteLength = computed<number>(() => supplementNote.value.length);

  const overSupplementNoteLimit = computed<boolean>(
    () => supplementNoteLength.value > params.supplementNoteMaxLength
  );

  // 報告理由・補足はいずれも任意のため、上限超過と送信中以外は常に送信できる（AC-002）。
  const submittable = computed<boolean>(() => !isSubmitting.value && !overSupplementNoteLimit.value);

  const isSelected = (key: number): boolean => reasonKeys.value.includes(key);

  const toggleReason = (key: number, selected: boolean): void => {
    if (selected) {
      if (!isSelected(key)) reasonKeys.value.push(key);
      return;
    }

    reasonKeys.value = reasonKeys.value.filter((selectedKey) => selectedKey !== key);
  };

  const clearError = (): void => {
    errorMessage.value = null;
    supplementNoteError.value = null;
  };

  const applyError = (error: unknown): void => {
    const code = extractErrorCode(error);

    if (code === "supplement_note_too_long") {
      supplementNoteError.value = resolveErrorMessage(error);
      return;
    }

    // 422 系は入力内容を保持したままダイアログ内に表示する（E-18）。
    errorMessage.value = resolveErrorMessage(error);
  };

  const submit = async (): Promise<HighRiskReportResult | null> => {
    // 送信ボタンの二度押しによる意図しない連続送信だけを抑止する。
    if (isSubmitting.value) return null;

    clearError();

    if (overSupplementNoteLimit.value) {
      supplementNoteError.value = ERROR_MESSAGES.supplement_note_too_long;

      return null;
    }

    isSubmitting.value = true;
    try {
      return await highRiskReportsApi.create({
        consultable_type: params.consultableType,
        consultable_id: params.consultableId,
        risk_assessment_id: params.riskAssessmentId,
        // 同一報告内での理由重複はサーバー側のUNIQUE制約に抵触するため取り除く。
        reason_keys: [...new Set(reasonKeys.value)],
        supplement_note: supplementNote.value
      });
    } catch (error) {
      applyError(error);

      return null;
    } finally {
      isSubmitting.value = false;
    }
  };

  return reactive({
    reasonKeys,
    supplementNote,
    errorMessage,
    supplementNoteError,
    isSubmitting,
    supplementNoteLength,
    overSupplementNoteLimit,
    submittable,
    isSelected,
    toggleReason,
    clearError,
    submit
  });
};
